import React, { useState } from "react";
import useAdminMutation from "@/hooks/useAdminMutation";
import useRLContext from "@/hooks/useRLContext";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLOperatorTensionPresetDialog — RACKET-LAB-ROLE-4 (Feb 2026).
 *
 * Modal admin-only block to view / edit the per-operator default
 * stringing-tension preset (`default_tension_unit`,
 * `default_main_tension`, `default_cross_tension` on the
 * `racket_lab_operators` document).
 *
 * Behaviour:
 *   - admin / staff (workforce write strict gate) can edit & save.
 *   - shop_manager / operator / qa_manager / qa_tester see READ-ONLY.
 *   - Empty state when all three fields are null.
 *   - Saving PATCHes the operator with only the preset fields; other
 *     keys remain untouched (the backend uses `exclude_unset=True`).
 *
 * The HIERARCHY documented for the future ticket flow is:
 *   1) manual edit (always wins);
 *   2) customer/profile/QR preset (BLOCKED — future);
 *   3) shop preset (RACKET-LAB-ROLE-2);
 *   4) operator/technician preset (this slice — fallback only when no
 *      shop preset applies);
 *   5) empty.
 *
 * No customer-facing surface. No real notifications. No migrations.
 */
const TENSION_UNITS = ["kg", "lbs"];

const RLOperatorTensionPresetDialog = ({ operator, open, onClose, onSaved }) => {
  const t = useRacketLabAdminT();
  const { ctx } = useRLContext();
  const { run, loading } = useAdminMutation();
  const canEdit = ctx?.profile === "admin" || ctx?.profile === "staff";

  const [unit, setUnit] = useState(operator?.default_tension_unit || "");
  const [main, setMain] = useState(
    operator?.default_main_tension == null ? "" : String(operator.default_main_tension)
  );
  const [cross, setCross] = useState(
    operator?.default_cross_tension == null ? "" : String(operator.default_cross_tension)
  );

  if (!open || !operator) return null;

  const hasPreset =
    operator.default_tension_unit != null ||
    operator.default_main_tension != null ||
    operator.default_cross_tension != null;

  const onSave = async () => {
    const payload = {
      default_tension_unit: unit || null,
      default_main_tension: main === "" ? null : parseInt(main, 10),
      default_cross_tension: cross === "" ? null : parseInt(cross, 10),
    };
    const updated = await run(
      "patch", `/admin/racket-lab/operators/${operator.id}`, payload,
    );
    if (updated) {
      onSaved?.(updated);
      onClose?.();
    }
  };

  const onClear = async () => {
    const payload = {
      default_tension_unit: null,
      default_main_tension: null,
      default_cross_tension: null,
    };
    const updated = await run(
      "patch", `/admin/racket-lab/operators/${operator.id}`, payload,
    );
    if (updated) {
      onSaved?.(updated);
      onClose?.();
    }
  };

  return (
    <div
      data-testid="rl-operator-tension-preset-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-md border border-slate-200 bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-baseline justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              {t("operators.tension_preset.title")}
            </h3>
            <p className="text-[10px] text-slate-500">
              {operator.display_label || operator.id}
            </p>
          </div>
          <span className="text-[10px] uppercase tracking-wide text-slate-400">
            {t("operators.tension_preset.admin_only")}
          </span>
        </header>

        <p className="mb-3 text-[11px] text-slate-500">
          {t("operators.tension_preset.hint")}
        </p>

        {!hasPreset && (
          <p
            data-testid="rl-operator-tension-preset-empty"
            className="mb-3 rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500"
          >
            {t("operators.tension_preset.empty")}
          </p>
        )}

        {!canEdit && hasPreset && (
          <ul
            data-testid="rl-operator-tension-preset-readonly"
            className="mb-3 grid grid-cols-3 gap-3 text-xs"
          >
            <li>
              <p className="text-[10px] uppercase tracking-wide text-slate-500">
                {t("operators.tension_preset.unit")}
              </p>
              <p className="font-semibold">{operator.default_tension_unit ?? "—"}</p>
            </li>
            <li>
              <p className="text-[10px] uppercase tracking-wide text-slate-500">
                {t("operators.tension_preset.main")}
              </p>
              <p className="font-semibold">{operator.default_main_tension ?? "—"}</p>
            </li>
            <li>
              <p className="text-[10px] uppercase tracking-wide text-slate-500">
                {t("operators.tension_preset.cross")}
              </p>
              <p className="font-semibold">{operator.default_cross_tension ?? "—"}</p>
            </li>
          </ul>
        )}

        {canEdit && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">
                {t("operators.tension_preset.unit")}
              </span>
              <select
                data-testid="rl-operator-tension-preset-unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              >
                <option value="">—</option>
                {TENSION_UNITS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">
                {t("operators.tension_preset.main")}
              </span>
              <input
                data-testid="rl-operator-tension-preset-main"
                type="number"
                min={1}
                max={80}
                value={main}
                onChange={(e) => setMain(e.target.value)}
                className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                placeholder="—"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">
                {t("operators.tension_preset.cross")}
              </span>
              <input
                data-testid="rl-operator-tension-preset-cross"
                type="number"
                min={1}
                max={80}
                value={cross}
                onChange={(e) => setCross(e.target.value)}
                className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                placeholder="—"
              />
            </label>
          </div>
        )}

        <footer className="mt-4 flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
          <button
            type="button"
            data-testid="rl-operator-tension-preset-cancel"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            {t("operators.tension_preset.cancel")}
          </button>
          {canEdit && hasPreset && (
            <button
              type="button"
              data-testid="rl-operator-tension-preset-clear"
              onClick={onClear}
              disabled={loading}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed"
            >
              {t("operators.tension_preset.clear")}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              data-testid="rl-operator-tension-preset-save"
              onClick={onSave}
              disabled={loading}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {loading ? t("operators.tension_preset.saving") : t("operators.tension_preset.save")}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default RLOperatorTensionPresetDialog;
